import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Receipt, 
  CreditCard, 
  Trophy, 
  BarChart3, 
  Percent,
  ChevronRight,
  Sparkles,
  Check,
  ArrowRight,
  Smartphone,
  Monitor,
  Apple
} from 'lucide-react';

const FeatureItem = ({ feature, index, isActive, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={onClick}
      className={`group relative cursor-pointer overflow-hidden rounded-2xl transition-all duration-500 ${
        isActive ? 'bg-white shadow-2xl' : 'bg-white/10 hover:bg-white/20'
      }`}
    >
      {/* Borda gradiente animada quando ativo */}
      {isActive && (
        <motion.div
          layoutId="activeBorder"
          className="absolute inset-0 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, #10b981, #06b6d4, #10b981)',
            padding: '2px',
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className="w-full h-full bg-white rounded-2xl" />
        </motion.div>
      )}

      <div className={`relative z-10 p-5 flex items-center gap-4 ${isActive ? 'bg-white rounded-2xl' : ''}`}>
        {/* Ícone */}
        <motion.div
          animate={{
            scale: isActive ? 1.1 : 1,
            rotate: isActive ? [0, -5, 5, 0] : 0,
          }}
          transition={{ duration: 0.5 }}
          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isActive 
              ? 'bg-gradient-to-br from-[#10b981] to-[#059669] shadow-lg shadow-green-500/30' 
              : 'bg-white/20'
          }`}
        >
          <feature.icon 
            size={24} 
            className={isActive ? 'text-white' : 'text-white/80'}
          />
        </motion.div>

        {/* Conteúdo */}
        <div className="flex-grow min-w-0">
          <h4 className={`font-semibold text-lg transition-colors duration-300 ${
            isActive ? 'text-[#2C5E50]' : 'text-white'
          }`}>
            {feature.title}
          </h4>
          
          {/* Descrição expandida quando ativo */}
          <AnimatePresence>
            {isActive && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="text-gray-600 text-sm mt-2 leading-relaxed"
              >
                {feature.description}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Seta */}
        <motion.div
          animate={{
            x: isActive ? 5 : 0,
            scale: isActive ? 1.2 : 1,
          }}
          className={`flex-shrink-0 ${isActive ? 'text-[#10b981]' : 'text-white/60'}`}
        >
          <ChevronRight size={24} />
        </motion.div>
      </div>

      {/* Linha de progresso quando ativo */}
      {isActive && (
        <motion.div
          className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#10b981] to-[#06b6d4]"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ duration: 5, ease: 'linear' }}
        />
      )}
    </motion.div>
  );
};

const MaisMeiFeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const features = [
    {
      icon: FileText,
      title: 'Guias DAS em dia',
      description: 'Acesso às guias DAS com o código "copia e cola", além de lembretes de vencimento que te ajudam a manter o pagamento do DAS em dia.',
      image: '📋',
      color: '#10b981',
    },
    {
      icon: Receipt,
      title: 'Emita suas Notas Fiscais',
      description: 'Emissão de notas fiscais de forma simples e rápida. Tudo integrado para facilitar sua gestão.',
      image: '🧾',
      color: '#06b6d4',
    },
    {
      icon: CreditCard,
      title: 'Fácil acesso ao seu cartão CNPJ',
      description: 'Tenha seu cartão CNPJ sempre à mão, pronto para apresentar quando precisar.',
      image: '💳',
      color: '#8b5cf6',
    },
    {
      icon: Trophy,
      title: 'Conheça seus benefícios MEI',
      description: 'Descubra todos os benefícios que você tem direito como MEI, incluindo aposentadoria e auxílios.',
      image: '🏆',
      color: '#f59e0b',
    },
    {
      icon: BarChart3,
      title: 'Faça sua declaração anual',
      description: 'Declaração anual simplificada com preenchimento automático. Sem complicação.',
      image: '📊',
      color: '#ec4899',
    },
    {
      icon: Percent,
      title: 'Parcele sua Dívida Ativa da União',
      description: 'Regularize suas pendências com facilidade. Parcelamento direto pelo app.',
      image: '💰',
      color: '#ef4444',
    },
  ];

  // Auto-play dos features
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, features.length]);

  const handleFeatureClick = (index) => {
    setIsAutoPlaying(false);
    setActiveFeature(index);
    // Retoma auto-play após 15 segundos
    setTimeout(() => setIsAutoPlaying(true), 15000);
  };

  return (
    <section className="relative py-24 overflow-hidden">
      {/* Background gradiente azul */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, #0369a1 0%, #0284c7 30%, #0ea5e9 70%, #38bdf8 100%)',
        }}
      />

      {/* Padrões decorativos */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Círculos flutuantes */}
        <motion.div
          animate={{ 
            y: [0, -30, 0],
            x: [0, 20, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
          }}
        />
        <motion.div
          animate={{ 
            y: [0, 40, 0],
            x: [0, -30, 0],
            scale: [1.1, 1, 1.1],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-15"
          style={{
            background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)',
          }}
        />

        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />

        {/* Ondas no fundo */}
        <svg 
          className="absolute bottom-0 left-0 right-0 opacity-20"
          viewBox="0 0 1440 200"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M0,100 C360,150 720,50 1080,100 C1260,125 1380,75 1440,100 L1440,200 L0,200 Z"
            fill="rgba(255,255,255,0.3)"
            animate={{
              d: [
                "M0,100 C360,150 720,50 1080,100 C1260,125 1380,75 1440,100 L1440,200 L0,200 Z",
                "M0,80 C360,50 720,150 1080,80 C1260,60 1380,120 1440,80 L1440,200 L0,200 Z",
                "M0,100 C360,150 720,50 1080,100 C1260,125 1380,75 1440,100 L1440,200 L0,200 Z",
              ],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Lado esquerdo - Conteúdo */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <motion.span
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <Sparkles size={16} className="text-[#d9f99d]" />
              </motion.span>
              <span className="text-sm font-medium text-white">
                Tudo em um só lugar
              </span>
            </motion.div>

            {/* Título */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
            >
              USE A{' '}
              <span 
                className="relative inline-block"
                style={{
                  background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                MEUmei
                <motion.span
                  className="absolute -bottom-1 left-0 right-0 h-1 rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, #d9f99d, #bef264)',
                  }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                />
              </span>
              {' '}PARA FAZER MAIS
            </motion.h2>

            {/* Descrição */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-lg mb-8 leading-relaxed"
            >
              Todos os recursos que você precisa para gerenciar seu MEI em um 
              só lugar. E o melhor, 0800: sem pagar nada por isso!
            </motion.p>

            {/* Botões de plataforma */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 mb-8"
            >
              {[
                { icon: Apple, label: 'App Store', sublabel: 'Disponível na' },
                { icon: Smartphone, label: 'Google Play', sublabel: 'Disponível no' },
                { icon: Monitor, label: 'Versão Web', sublabel: 'Acesse pelo' },
              ].map((platform, index) => (
                <motion.button
                  key={platform.label}
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-3 px-5 py-3 rounded-xl transition-all duration-300"
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                >
                  <platform.icon size={28} className="text-white" />
                  <div className="text-left">
                    <div className="text-white/70 text-xs">{platform.sublabel}</div>
                    <div className="text-white font-semibold">{platform.label}</div>
                  </div>
                </motion.button>
              ))}
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="flex items-center gap-6"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0, x: -20 }}
                    whileInView={{ scale: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7 + i * 0.1 }}
                    className="w-10 h-10 rounded-full border-2 border-white overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, hsl(${i * 60}, 70%, 60%) 0%, hsl(${i * 60 + 30}, 70%, 50%) 100%)`,
                    }}
                  />
                ))}
              </div>
              <div className="text-white">
                <div className="font-bold text-lg">Muitos</div>
                <div className="text-white/70 text-sm">MEIs já usam</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Lado direito - Lista de features */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-3"
          >
            {features.map((feature, index) => (
              <FeatureItem
                key={feature.title}
                feature={feature}
                index={index}
                isActive={activeFeature === index}
                onClick={() => handleFeatureClick(index)}
              />
            ))}

            {/* Indicador de progresso geral */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex justify-center gap-2 pt-4"
            >
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleFeatureClick(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeFeature === index
                      ? 'w-8 bg-white'
                      : 'w-2 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-20 text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.href = 'https://app.meumei.net/register'}
            className="group relative inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-lg overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #d9f99d 0%, #bef264 100%)',
              boxShadow: '0 15px 50px rgba(217, 249, 157, 0.4)',
            }}
          >
            {/* Efeito de brilho */}
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
              initial={{ x: '-100%' }}
              whileHover={{ x: '100%' }}
              transition={{ duration: 0.6 }}
            />
            <span className="relative z-10 text-[#2C5E50]">Começar gratuitamente</span>
            <motion.span
              className="relative z-10 text-[#2C5E50]"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={24} />
            </motion.span>
          </motion.button>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="mt-4 text-white/70 flex items-center justify-center gap-2"
          >
            <Check size={18} className="text-[#d9f99d]" />
            Experimente no Cartão de Crédito • Cancele quando quiser
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default MaisMeiFeaturesSection;